import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp
} from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { getDate, getFormattedDate, isObjectEmpty, getUTCDate } from '../../../utils/helpers';
import { createTFU, TFU, updateTFU, TicketFollowUpResult, TicketFollowUpEscalation } from '../../../store/TFU';
import { PageCard } from '../CardWithTitle';
import { Icon } from '../../Atoms/Icon';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { isData, isObserver } from '../../../store/User';
import { CustomDateInput } from '../ETU/CustomDateInput';
import { TextSpan } from '../../Atoms/Text';
import { getTFUSettingsSubject, fetchTFUSettings, TFUSetting } from '../../../store/TFUSetting';

export interface TFUMiniTableParams {
  id: string;
  fetchFunction: () => Promise<void>;
  removeFunction?: (id: string, drawer?: boolean) => Promise<void>;
  dataSubject: () => BehaviorSubject<TFU[]>;
  loadingSubject: () => BehaviorSubject<boolean>;
  showCreate?: boolean;
  ticketStatus?: string;
  drawer?: boolean;
}

export const TFUMiniTable = ({
  id,
  fetchFunction,
  removeFunction,
  dataSubject,
  loadingSubject,
  showCreate = true,
  ticketStatus,
  drawer = false
}: TFUMiniTableParams) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [tfuSettings, setTfuSettings] = useState<TFUSetting[]>([]);
  const [tempEscalation, setTempEscalation] = useState<TicketFollowUpEscalation | null>(null);

  const getCalculatedDueDate = useMemo(
    () =>
      (escalation: TicketFollowUpEscalation): string | null => {
        const setting = tfuSettings.find((s) => s.escalation === escalation);
        if (setting) {
          const today = new Date();
          today.setDate(today.getDate() + setting.dueOffset);
          return getUTCDate(today, 0).toISOString();
        }
        return null;
      },
    [tfuSettings]
  );

  const handleRemove = async (id: GridRowId) => {
    if (removeFunction) {
      removeFunction(id as string, drawer);
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'dueDate' } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows((rows) => rows.filter((row) => row.id !== id));
    handleRemove(id);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });

    const editedRow = rows.find((row) => (row as TFU).id === id);
    if (editedRow?.isNew) {
      setRows((rows) => rows.filter((row) => (row as TFU).id !== id));
    }
  };

  const handleAddClick = () => {
    const newTicketId = v4();
    const defaultDueDate = getCalculatedDueDate(TicketFollowUpEscalation.E0);

    setRows((oldRows) => [
      {
        id: newTicketId,
        dueDate: defaultDueDate,
        ticketId: id,
        note: '',
        result: TicketFollowUpResult.PENDING,
        escalation: TicketFollowUpEscalation.E0,
        isNew: true
      },
      ...oldRows
    ]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newTicketId]: { mode: GridRowModes.Edit, fieldToFocus: 'dueDate' }
    }));
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const index = rows.findIndex((row) => row.id === newRow.id);
    if (index === -1) return newRow;

    const updatedRow = { ...newRow, isNew: !!newRow.isNew, isCreating: !!newRow.isNew };
    setRows((rows) => rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    const tfuUpdate = JSON.parse(JSON.stringify(updatedRow));
    if (tfuUpdate.isNew) delete tfuUpdate.id;
    delete tfuUpdate.isNew;
    delete tfuUpdate.isCreating;

    if (tfuUpdate.id) updateTFU(tfuUpdate as TFU, drawer);
    else createTFU(tfuUpdate as TFU, drawer);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params: GridRowEditStopParams, event: unknown) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event as any).defaultMuiPrevented = true;
    }
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      setRows((rows) => {
        const newRows = rows.filter((row) => row.isNew && !row.isCreating);
        return [...newRows, ...r];
      });
    });
    const assetLoadingSub = loadingSubject().subscribe((l) => setLoading(l));
    const tfuSettingsSub = getTFUSettingsSubject().subscribe((settings) => {
      setTfuSettings(settings);
    });

    fetchFunction();
    fetchTFUSettings();

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (assetLoadingSub) assetLoadingSub.unsubscribe();
      if (tfuSettingsSub) tfuSettingsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (ticketStatus == 'Resolved' && rows.length > 0) {
      setRowModesModel({});
      fetchFunction();
    }
  }, [ticketStatus]);

  const tableCols: GridColDef[] = [
    {
      headerName: 'Due',
      field: 'dueDate',
      flex: 1,
      type: 'date',
      sortable: true,
      editable: true,
      valueGetter: (value) => {
        return value ? getDate(value) : '';
      },
      valueFormatter: (value) => {
        return getFormattedDate(value);
      },
      sortComparator: (v1, v2) => {
        return getDate(v1).getTime() - getDate(v2).getTime();
      },
      renderEditCell: (params: GridRenderCellParams) => <CustomDateInput {...params} />,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const currentEscalationInRow = tempEscalation || params.row.escalation;
        const newEscalationFromOtherFields = params.otherFieldsProps?.escalation?.value;
        if (newEscalationFromOtherFields !== undefined && newEscalationFromOtherFields !== currentEscalationInRow) {
          setTempEscalation(newEscalationFromOtherFields);
          const calculatedDateString = getCalculatedDueDate(newEscalationFromOtherFields);
          const updatedDueDate = calculatedDateString ? getDate(calculatedDateString) : null;
          return { ...params.props, value: updatedDueDate };
        } else {
          return { ...params.props };
        }
      }
    },
    {
      headerName: 'Result',
      field: 'result',
      flex: 1,
      type: 'singleSelect',
      editable: true,
      sortable: true,
      filterable: false,
      valueOptions: Object.values(TicketFollowUpResult)
    },
    {
      headerName: 'Note',
      field: 'note',
      flex: 2,
      type: 'string',
      editable: true,
      sortable: true,
      filterable: false
    },
    {
      headerName: 'Escalation',
      field: 'escalation',
      flex: 0.5,
      type: 'singleSelect',
      editable: true,
      sortable: true,
      filterable: false,
      valueOptions: Object.values(TicketFollowUpEscalation)
    }
  ];

  if (!isObserver() && !isData()) {
    tableCols.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon sx={{ color: 'primary.main' }} />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon color="buttonGrey" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon color="buttonGrey" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />
        ];
      }
    });
  }

  const cardActions = () => {
    return isObserver() || isData() || !showCreate ? (
      <></>
    ) : (
      <button
        className="flex items-center space-x-2"
        disabled={!isObjectEmpty(rowModesModel)}
        onClick={handleAddClick}
        data-cy="new-tfu-button"
        data-testid="new-tfu-button">
        <Icon type="plus" />
        <TextSpan
          size="sm14"
          color={!isObjectEmpty(rowModesModel) ? 'grey5' : 'indigo'}>
          New Follow-Up
        </TextSpan>
      </button>
    );
  };

  return (
    <div
      className="w-full"
      data-testid="tfu-mini-table-container">
      <PageCard
        title={`Follow-Ups (${rows.length})`}
        cardActions={cardActions()}>
        <Fragment>
          {loading && <ComponentSpinner label="Loading TFU Data" />}
          {!loading && (
            <DataGridPro
              rows={rows}
              sx={{
                ...DEFAULT_MINI_TABLE_SX_STYLES,
                maxHeight: '30rem',
                overflow: 'auto',
                border: 'unset'
              }}
              rowHeight={40}
              columnHeaderHeight={40}
              showCellVerticalBorder
              hideFooter
              showColumnVerticalBorder
              initialState={{
                sorting: {
                  sortModel: [{ field: 'dueDate', sort: 'desc' }]
                }
              }}
              columns={tableCols}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              processRowUpdate={processRowUpdate}
              onRowEditStop={handleRowEditStop}
            />
          )}
        </Fragment>
      </PageCard>
    </div>
  );
};
